import { localeOf } from "./format.ts";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "./language-preference.ts";
import type { Language, SupportedLanguage } from "./language-preference.ts";
import { PRODUCT_NAME } from "./product-name.ts";

// The catalogue every module's `model/<module>-copy.ts` exports: the English
// catalogue, written `as const` so its words are types, and one translation of
// it per other shipped language. A value is a line ("Remove {name}?") or a
// plural (`{ one: "1 member", other: "{count} members" }`): one form per
// category `Intl.PluralRules` names, `other` required, and a form a language's
// grammar asks for but the line leaves out reads as `other`.
//
// A `{name}` in a line is a fill-in the caller must pass (`t("removeTitle",
// { name })`); the English line decides which fill-ins a key takes, so a
// forgotten one fails the typecheck, not a screen. Only `{count}` takes a
// number, written the way the active language writes numbers; any other
// fill-in is text the caller already formatted (`useFormat`), so a year or an
// id never reads "2,026". `{product}` is the product's name (`product-name.ts`):
// every line may use it and no caller passes it.

/** The forms of one plural line, by the CLDR category `Intl.PluralRules` picks. */
export type PluralForms = { readonly other: string } & Partial<
  Readonly<Record<"zero" | "one" | "two" | "few" | "many", string>>
>;
export type CatalogValue = string | PluralForms;
export type Catalog = Readonly<Record<string, CatalogValue>>;

/** Another language's catalogue for the same keys: a line stays a line, a plural stays a plural. */
export type Translation<TSource extends Catalog> = {
  readonly [TKey in keyof TSource]: TSource[TKey] extends string ? string : PluralForms;
};

export type Catalogs<TSource extends Catalog> = Readonly<Record<typeof DEFAULT_LANGUAGE, TSource>> &
  Readonly<Record<Exclude<SupportedLanguage, typeof DEFAULT_LANGUAGE>, Translation<TSource>>>;

type HolesIn<TLine> = TLine extends `${string}{${infer Name}}${infer Rest}` ? Name | HolesIn<Rest> : never;

/** The fill-ins one value takes: every `{name}` in it but `{product}`, and `count` for a plural. */
type Holes<TValue> = Exclude<
  TValue extends string
    ? HolesIn<TValue>
    : TValue extends PluralForms
      ? HolesIn<TValue[keyof TValue] & string> | "count"
      : never,
  "product"
>;

export type Fill<TValue> = {
  readonly [Name in Holes<TValue>]: Name extends "count" ? number : string;
};

type FillArgs<TValue> = [Holes<TValue>] extends [never] ? [] : [fill: Fill<TValue>];

/** Reads one key in the active language; a key with fill-ins takes them as its second argument. */
export type Translate<TSource extends Catalog> = <TKey extends keyof TSource & string>(
  key: TKey,
  ...fill: FillArgs<TSource[TKey]>
) => string;

/** The keys that read without any fill-in: what a table of labels may hold. */
export type PlainKey<TSource extends Catalog> = {
  [TKey in keyof TSource & string]: [Holes<TSource[TKey]>] extends [never] ? TKey : never;
}[keyof TSource & string];

type AnyFill = Readonly<Record<string, string | number>>;

/** The shipped language a stored one reads in: itself when it has a catalogue, else the default. */
export function shippedLanguage(language: Language): SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language)
    ? (language as SupportedLanguage)
    : DEFAULT_LANGUAGE;
}

const pluralRules = new Map<SupportedLanguage, Intl.PluralRules>();
const numberFormats = new Map<SupportedLanguage, Intl.NumberFormat>();

function pluralForm(forms: PluralForms, params: { language: SupportedLanguage; count: number }): string {
  let rules = pluralRules.get(params.language);
  if (!rules) {
    rules = new Intl.PluralRules(localeOf(params.language));
    pluralRules.set(params.language, rules);
  }
  const category = rules.select(params.count);
  return (category === "other" ? undefined : forms[category]) ?? forms.other;
}

function filled(line: string, params: { language: SupportedLanguage; fill: AnyFill }): string {
  let numbers = numberFormats.get(params.language);
  if (!numbers) {
    numbers = new Intl.NumberFormat(localeOf(params.language));
    numberFormats.set(params.language, numbers);
  }
  const format = numbers;
  return line.replace(/\{(\w+)\}/g, (hole, name: string) => {
    const value = params.fill[name] ?? (name === "product" ? PRODUCT_NAME : undefined);
    if (value === undefined) return hole;
    return name === "count" && typeof value === "number" ? format.format(value) : String(value);
  });
}

/**
 * Reads one key in `language`, falling back to the default language's copy of
 * the same key when that language has none (no catalogue for it at all, or a
 * catalogue missing just that key). A plural picks its form by the grammar of
 * the language its words are in; `{count}` reads the way the active language
 * writes numbers ("12,345" in English, "12.345" in Spanish).
 */
export function translate<TSource extends Catalog>(
  catalogs: Catalogs<TSource>,
  language: Language,
  params: { readonly key: keyof TSource & string; readonly fill?: AnyFill | undefined },
): string {
  const active = shippedLanguage(language);
  const direct = (catalogs as Readonly<Record<string, Catalog | undefined>>)[language]?.[params.key];
  const entry = direct ?? catalogs[DEFAULT_LANGUAGE][params.key];
  const fill = params.fill ?? {};
  // Unreachable through `Translate`, which only takes keys the English catalogue has.
  if (entry === undefined) return params.key;
  const line =
    typeof entry === "string"
      ? entry
      : pluralForm(entry, {
          language: direct === undefined ? DEFAULT_LANGUAGE : active,
          count: Number(fill["count"] ?? 1),
        });
  return filled(line, { language: active, fill });
}

/** The same reader as `useTranslations`, outside React: a test, or a caller handed a language. */
export function translatorFor<TSource extends Catalog>(
  catalogs: Catalogs<TSource>,
  language: Language,
): Translate<TSource> {
  return (key, ...fill) => translate(catalogs, language, { key, fill: fill[0] as AnyFill | undefined });
}

function holesOf(value: CatalogValue): string {
  const lines = typeof value === "string" ? [value] : Object.values(value);
  const names = new Set(lines.flatMap((line) => [...line.matchAll(/\{(\w+)\}/g)].map((match) => match[1])));
  // `{product}` is filled for every line, so a language may name the product where English does not.
  names.delete("product");
  return [...names].toSorted().join(",");
}

/**
 * Every key the default language's catalogue carries that a shipped
 * language's catalogue is missing: empty for every language when the
 * catalogue is complete. The test a module's `model/__tests__/*-copy.unit.test.ts`
 * runs over its own `CATALOGS` so a catalogue that falls behind fails `pnpm test`,
 * not a screen in production.
 */
export function missingKeys<TSource extends Catalog>(
  catalogs: Catalogs<TSource>,
): Readonly<Record<SupportedLanguage, readonly string[]>> {
  return perLanguage(catalogs, (own, key) => !(key in own));
}

/**
 * Every key whose translation names different fill-ins than its English line,
 * or is a line where English is a plural (or the other way round): a Spanish
 * "Quitar a {nombre}" for "Remove {name}" would show the brace to the reader.
 * `{product}` is not counted: no caller passes it, so it may appear in one language only.
 */
export function mismatchedFills<TSource extends Catalog>(
  catalogs: Catalogs<TSource>,
): Readonly<Record<SupportedLanguage, readonly string[]>> {
  const source: Catalog = catalogs[DEFAULT_LANGUAGE];
  return perLanguage(catalogs, (own, key) => {
    const value = own[key];
    if (value === undefined) return false;
    const english = source[key] as CatalogValue;
    return typeof value !== typeof english || holesOf(value) !== holesOf(english);
  });
}

function perLanguage<TSource extends Catalog>(
  catalogs: Catalogs<TSource>,
  offends: (own: Catalog, key: string) => boolean,
): Readonly<Record<SupportedLanguage, readonly string[]>> {
  const keys = Object.keys(catalogs[DEFAULT_LANGUAGE]);
  const result = {} as Record<SupportedLanguage, readonly string[]>;
  for (const language of SUPPORTED_LANGUAGES) {
    const own: Catalog = catalogs[language];
    result[language] = keys.filter((key) => offends(own, key));
  }
  return result;
}
